import {Component, OnInit, ViewChild} from '@angular/core';
import {AggregateService, ColumnChooserService, GridComponent, GroupService, RowSelectEventArgs, ToolbarItems, ToolbarService,} from '@syncfusion/ej2-angular-grids';
import {ServiceAppModels} from 'src/app/models/serviceapp-models';
import {MatDialog} from '@angular/material/dialog';
import {DatumFormatPipe} from 'src/app/pipes/datum-format.pipe';
import {ZeitMutationComponent} from './zeit-mutation/zeit-mutation.component';
import {MatTabChangeEvent} from '@angular/material/tabs';
import {UntypedFormControl} from '@angular/forms';
import {SwissSettingsService} from 'src/app/services/swiss-settings.service';
import {ServiceAppService} from 'src/app/services/service-app.service';
import {MessageBoxService} from 'src/app/services/message-box.service';
import {L10n, loadCldr, setCulture,} from '@syncfusion/ej2-base';
import {ServiceAppComponent} from '../service-app.component';
import {UserAuthenticationService} from 'src/app/services/user-authentication.service';
import {ToaterMessagesService} from '../../../time-app/services/toaster-messages.service';
import {LocalStorageService} from '../../../local-storage.service';
import {BehaviorSubject, combineLatest, Observable, of, switchMap, tap} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Orders} from '../../../time-app/models/order-model';
import {DataService} from '../../../time-app/services/data.service';
import SA_ZeitZeile = ServiceAppModels.SA_ZeitZeile;
import SA_Objekt = ServiceAppModels.SA_Objekt;
import SA_DataTimeMutation = ServiceAppModels.SA_DataTimeMutation;

@Component({
    selector: 'app-zeiten', templateUrl: './zeiten.component.html', styleUrls: ['./zeiten.component.scss'], providers: [AggregateService, GroupService, ToolbarService, ColumnChooserService],
})
export class ZeitenComponent implements OnInit {
    @ViewChild('gridZeiten') public grid?: GridComponent;
    @ViewChild('gridTagesZeiten') public gridDay?: GridComponent;
    @ViewChild('gridZeitkontrolle') public gridTimecontrol?: GridComponent;
    public currTerminFinished$: BehaviorSubject<boolean> = new BehaviorSubject(true); // Auftrag abgeschlossen
    public currDayClosed$: BehaviorSubject<boolean> = new BehaviorSubject(true); // Tag abgeschlossen
    public timeUploaded$: BehaviorSubject<boolean> = new BehaviorSubject(true); // Aktueller Zeiteintrag ist übermittelt
    public isTimeSelected$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    public allowModifyHours$: Observable<boolean> = combineLatest({
        isTimeSelected: this.isTimeSelected$,
        isFinished: this.currTerminFinished$,
        isClosed: this.currDayClosed$,
        isUploaded: this.timeUploaded$
    }).pipe(
        switchMap((args) => {
            return of(args.isTimeSelected && !args.isFinished && !args.isClosed && !args.isUploaded);
        })
    );

    public sortOptions: object = {
        columns: [
            {field: 'datumStrEN', direction: 'Descending'},
            {field: 'zeiten', direction: 'Ascending'}],
    };
    public dayDateValue: Date = new Date();
    public dayDate: UntypedFormControl;
    public totalTagesAufwand = 0.0;
    public groupSettings: { [x: string]: Object } = {
        showDropArea: false,
        columns: ['datumStrEN'],
    };
    public toolbarOptions?: ToolbarItems[];

    public filtervalue = 'Alle Tage';
    protected zeitZeilen$: BehaviorSubject<(SA_ZeitZeile & { isTimeEntry?: boolean })[]> = new BehaviorSubject([] as SA_ZeitZeile[]);
    protected zeitKontrollZeilen$: BehaviorSubject<SA_ZeitZeile[]> = new BehaviorSubject([] as SA_ZeitZeile[]);
    private currTimeEntry?: ServiceAppModels.SA_TimeEntry;
    private currEmployeeID?: number;
    private currZeitZeile?: SA_ZeitZeile;
    private currTabIndex = 0;
    private currentLang = 'de';
    private timeOrders: BehaviorSubject<Orders[]> = new BehaviorSubject([] as Orders[]);

    constructor(
        private toasterMessagesService: ToaterMessagesService,
        private _datumFormatPipe: DatumFormatPipe,
        private svcSA: ServiceAppService,
        private svcMessageBox: MessageBoxService,
        public dialog: MatDialog,
        private swissSettings: SwissSettingsService,
        private userAuthService: UserAuthenticationService,
        private saComp: ServiceAppComponent,
        private localStorage: LocalStorageService,
        private dataService: DataService) {
        this.dayDateValue.setHours(0, 0, 0, 0);
        this.dayDate = new UntypedFormControl(this.dayDateValue);

        this.currEmployeeID = this.userAuthService.getCurrentAuthenticatedUser()!.employeeID;
        this.loadFilterSettings();

        this.svcSA.getTimesChanged$().pipe(
            takeUntilDestroyed(),
            tap(async () => this.refreshGrid())
        ).subscribe();
    }

    public ngOnInit(): void {
        this.toolbarOptions = ['ColumnChooser'];

        this.localStorage.get('language', 'SwissTimeEntry', true).then((res: any) => {
            this.currentLang = res;
        });
        requestAnimationFrame(async () => {
            await this.refreshGrid();
        });

        this.dataService.getAllOrders().then(timeOrders => this.timeOrders.next(timeOrders));
    }

    // Das Feld Kulanz erhält je nach Wert ein anderes Image
    protected imgSrc(value?: boolean): string {
        let htmlStr = 'cancel.png';
        if (value) {
            htmlStr = 'ok.png';
        }
        return htmlStr;
    }

    protected customAggregateLock = (sdata: any) => {
        return this.svcSA.getTimeControlDayState(sdata.key);
    }

    protected async changeDayControlState(sDatum: string): Promise<void> {
        sDatum = this._datumFormatPipe.convertDateStringEN2DateStringCH(sDatum);
        await this.svcSA.changeTimeControlDayState(sDatum);
    }

    protected formatDate(data: any): string {
        return this._datumFormatPipe.convertDateStringEN2DateStringCH(data.key);
    }

    protected valueAccess = (field: string, data: Object, column: Object) => {
        const value = (data as any)[field];
        return this._datumFormatPipe.convertDateStringEN2DateStringCH(value);
    }

    protected async onDayDateChange(value: string): Promise<void> {
        // Datum kommt als String. Bsp.: 14.05.2021
        if (value) {
            // Die Datumskonvertierung muss so gemacht werden
            const dateString = this._datumFormatPipe.transformOnlyDateEN(this.dayDate.value) + 'T00:00:00';
            this.dayDateValue = new Date(dateString);
            await this.loadTimeEntriesDay();
        }
    }

    protected async rowSelected(args: RowSelectEventArgs): Promise<void> {
        this.currZeitZeile = ((this.grid!.getSelectedRecords()[0]) as SA_ZeitZeile);
        await this.handleRowSelected();
    }

    protected async rowSelectedDay(args: RowSelectEventArgs): Promise<void> {
        this.currZeitZeile = ((this.gridDay!.getSelectedRecords()[0]) as SA_ZeitZeile);
        await this.handleRowSelected();
    }

    protected rowDeselected(args: RowSelectEventArgs): void {
        this.currTimeEntry = undefined;
        this.currZeitZeile = undefined;
        this.isTimeSelected$.next(false);
    }

    protected async rowSelectedControl(args: RowSelectEventArgs): Promise<void> {
        // Service-PosID der ausgewählten Zeile ermitteln
        this.currZeitZeile = ((this.gridTimecontrol!.getSelectedRecords()[0]) as SA_ZeitZeile);
        // Auftrag im Header anzeigen
        if (this.currZeitZeile) {
            this.svcSA.setTerminByServiceID(this.currZeitZeile.serviceID!);
            this.saComp.reloadAppointment(this.svcSA.getTermin());
        }
        await this.handleRowSelected();
    }

    protected async delWorkingHours(): Promise<void> {
        if (this.currZeitZeile?.uploaded) {
            return this.toasterMessagesService.toasterTranslateErrorMessages('Synchronisierte Zeiten können nicht gelöscht werden', 'Les temps synchronisés ne peuvent pas être supprimés', this.currentLang);
        }

        if (this.currTimeEntry?.uploadedToTimeControlEntries || this.currTimeEntry?.uploadedToTimeControlEntries) {
            return this.toasterMessagesService.toasterTranslateErrorMessages('Synchronisierte Zeiten können nicht gelöscht werden', 'Les temps synchronisés ne peuvent pas être supprimés', this.currentLang);
        }

        if (this.currDayClosed$.getValue()) {
            return this.toasterMessagesService.toasterTranslateErrorMessages('Der Tag ist bereits abgeschlossen', 'La journée est déjà terminée', this.currentLang);
        }

        if (this.currTerminFinished$.getValue()) {
            return this.toasterMessagesService.toasterTranslateErrorMessages('Der Termin ist bereits abgeschlossen', 'La rendez-vous est déjà terminée', this.currentLang);
        }

        if (!this.isTimeSelected$.getValue()) {
            return this.toasterMessagesService.toasterTranslateErrorMessages('Bitte wählen Sie eine Zeit aus', 'Veuillez choisir une heure', this.currentLang);
        }

        if (this.currZeitZeile?.uploaded) {
            return this.toasterMessagesService.toasterTranslateErrorMessages('Synchronisierte Zeiten können nicht gelöscht werden', 'Les temps synchronisés ne peuvent pas être supprimés', this.currentLang);
        }

        if (this.currTimeEntry && this.currZeitZeile) {
            const value = await this.svcMessageBox.askYesNoQuestion('MAIN_APP_DIALOG.DEL_WORKING_HOURS.msg', 'MAIN_APP_DIALOG.DEL_WORKING_HOURS.title', [this._datumFormatPipe.transformOnlyDate(this.currTimeEntry.startDate), this.currTimeEntry.activityDescription]);
            if (value) {
                await this.svcSA.delTimEntryInIndexedDB(this.currTimeEntry!, this.currZeitZeile!.servicePosID);
            }
        }
    }

    // Nach der Filterung muss das Total wieder neu berechnet werden.
    protected complete(args: any): any {
        if (args.requestType === 'filtering') {
            // Der Filter wurde ausgeführt
            if (this.swissSettings.getWebConfig().debugMode) {
                console.log('Aktuelle grid', this.gridDay);
            }
            if (this.gridDay && this.gridDay.currentViewData) {
                // Hier sind die angezeigten Zeilen drin
                this.totalTagesAufwand = 0.0;
                this.gridDay.currentViewData.forEach((elem) => (this.totalTagesAufwand += Number((elem as SA_ZeitZeile).zeitMenge)));
                if (this.swissSettings.getWebConfig().debugMode) {
                    console.log('Menge', this.totalTagesAufwand);
                }
                // Tagesaufwand aktualisieren
                this.customAggregateFn = () => String((Math.round(this.totalTagesAufwand * 100) / 100).toFixed(2)).replace(',', '.');
            }
        }
    }

    // Summe des Aufwandes für die Tagesübersicht
    protected customAggregateFn = () => {
        return String((Math.round(this.totalTagesAufwand * 100) / 100).toFixed(2)).replace(',', '.');
    }

    protected async changeFilter(args: any): Promise<void> {
        await this.loadTimeEntries();
    }

    protected saveFilterSettings(): void {
        this.svcSA.saveFilterSettings(this.filtervalue);
    }

    protected async editWorkingHours(): Promise<void> {
        if (this.isTimeSelected$.getValue() && !this.currTerminFinished$.getValue() && !this.currDayClosed$.getValue()) {
            let saAppointment: SA_Objekt | Orders | undefined = undefined;
            if (this.currTimeEntry?.serviceID) {
                saAppointment = this.svcSA.getTerminByServiceID(this.currTimeEntry.serviceID);
            } else if (this.currTimeEntry?.objectID) {
                const oID = this.currTimeEntry.objectID;
                saAppointment = (() => {
                    const appointment = this.svcSA.getObjektByObjektID(oID);
                    if (appointment) {
                        return appointment;
                    }
                    const timeOrder = this.timeOrders.getValue().find((t) => t.objectID == oID);
                    return timeOrder;
                })();
            }

            const data: SA_DataTimeMutation = {
                sA_Termin: saAppointment || this.svcSA.getTermin(),
                sA_Anwender: this.userAuthService.getCurrentAuthenticatedUser(),
                sA_TimeEntry: this.currTimeEntry,
            };

            this.dialog.open(ZeitMutationComponent, {
                disableClose: true, data, panelClass: 'swissdefault-dialog-container',
            }).afterClosed().subscribe({
                next: async (result) => {
                    if (result) {
                        this.currTimeEntry = result;
                        if (this.currTimeEntry && this.currZeitZeile) {
                            await this.svcSA.updTimeEntryInIndexedDB(this.currTimeEntry!, this.currZeitZeile?.servicePosID);
                            if (this.swissSettings.getWebConfig().debugMode) {
                                console.log('Dialog Resultat (sa_TimeEntry): ', this.currTimeEntry);
                            }
                            await this.loadTimeEntries();
                        }
                    }
                },
            });
        }
    }

    protected async dayBack(event: Event): Promise<void> {
        this.dayDateValue.setDate(this.dayDateValue.getDate() - 1);
        this.dayDate.setValue(this.dayDateValue);
        await this.loadTimeEntriesDay();
    }

    protected async dayForward(event: Event): Promise<void> {
        this.dayDateValue.setDate(this.dayDateValue.getDate() + 1);
        this.dayDate.setValue(this.dayDateValue);
        await this.loadTimeEntriesDay();
    }

    protected async timeTabChange(event: MatTabChangeEvent): Promise<void> {
        this.currTabIndex = event.index;
        await this.refreshGrid();
    }

    private async refreshGrid(): Promise<void> {
        if (this.currTabIndex === 1) {
            await this.loadTimeEntriesDay();
        } else {
            await this.loadTimeEntries();
        }
    }

    private async loadTimeEntriesDay(): Promise<void> {
        this.totalTagesAufwand = 0.0;
        if (await this.svcSA.loadTimeEntriesDayFromIndexedDB(this.dayDateValue)) {
            await this.getAuftragszeiten(false, false);
        }
    }

    private loadFilterSettings(): void {
        this.svcSA.loadFilterSettings().then((res) => {
            if (res) {
                this.filtervalue = res;
            }
        });
    }

    private async loadTimeEntries(): Promise<void> {
        await this.svcSA.loadTimeEntriesFromIndexedDB();
        if (this.currTabIndex != 1) {
            await this.getAuftragszeiten(!(this.currTabIndex == 2), this.currTabIndex == 2);
        }
    }

    private async getAuftragszeiten(terminBezogen: boolean, nurEigene: boolean): Promise<void> {
        const times: (SA_ZeitZeile & { isTimeEntry?: boolean })[] = [];
        const timeControl: SA_ZeitZeile[] = [];

        for (const auftragszeit of this.svcSA.getDBAuftragsZeiten(terminBezogen)) {
            const target = auftragszeit.serviceID ? 'serviceID' : 'objectID';
            let isTimeEntry = false;

            const auftragObj: string = (() => {
                const order = this.svcSA.getDBTermine().find((t) => t[target] == auftragszeit[target]);
                if (order) {
                    return `${order.objectNumber.trim()} ${order.objDescription} ${order.startTime ? this._datumFormatPipe.transformOnlyDate(order.startTime) : ''}`;
                }
                const timeOrder = this.timeOrders.getValue().find((t) => t.objectID == auftragszeit.objectID);
                isTimeEntry = !!timeOrder;
                return `${timeOrder?.objectNumber.trim()} ${timeOrder?.description}`;
            })();

            // startDate könnte verschiedene Formate haben, z.B. 2023-03-21 oder Wed Mar 22 2023 14:35:00 GMT+0100 (Mitteleuropäische Normalzeit)
            // deshalb konvertierung in einheitliches Datum
            let datum = auftragszeit.startDate;
            if (datum && datum.toString().length === 10) { // Format 2023-03-21
                datum = this._datumFormatPipe.convertStringENToDate(auftragszeit.startDate!.toString());
            }
            const datumStrEn = this._datumFormatPipe.transformOnlyDateEN(datum);
            const datumStrCh = this._datumFormatPipe.transformOnlyDate(datum);

            let zeitBeginn = '';
            let zeitEnde = '';
            let zeitVollstaendig = '';
            if (String(auftragszeit.startDate).length > 10) {
                // Zeit nur ermitteln, wenn es kein reines Datum ist
                zeitBeginn = this._datumFormatPipe.transformOnlyTime(auftragszeit.startDate);
                zeitEnde = this._datumFormatPipe.transformOnlyTime(auftragszeit.endDate);
                zeitVollstaendig = zeitBeginn + '-' + zeitEnde;
            }

            let spesen = '';
            if (auftragszeit.expenseID) {
                spesen = this.svcSA.getDBSpesen().filter((sp) => sp.expenseID == auftragszeit.expenseID)[0].description;
            }

            // Für jeden Mitarbeiter wird einen Eintrag gemacht
            const alleMitarbeiter: ServiceAppModels.SA_Employees[] = this.svcSA.getDBMitarbeiter();

            for (const mitarbeiter of auftragszeit.employeeIDS) {
                if (nurEigene) {
                    if (mitarbeiter != this.currEmployeeID) {
                        continue;
                    } // for-Schleife verlassen
                }

                const arbeiter = alleMitarbeiter.filter((m) => m.employeeID == mitarbeiter)[0];
                const arbeiterName = arbeiter?.firstName + ' ' + arbeiter?.lastName;

                let spesenMengeEinheit = '';
                if (auftragszeit.expenseAmount) {
                    spesenMengeEinheit = String(auftragszeit.expenseAmount);
                    if (auftragszeit.expenseUnit) {
                        spesenMengeEinheit += ' ' + auftragszeit.expenseUnit;
                    }
                }
                let spesenBetrag = '';
                if (auftragszeit.expensePrice) {
                    spesenBetrag = String(auftragszeit.expensePrice);
                }

                // Das '+' - Zeichen macht aus dem möglichen String-Wert eine Zahl bei auftragszeit.duration
                const zeile: (SA_ZeitZeile & { isTimeEntry?: boolean }) = {
                    servicePosID: auftragszeit.servicePosID,
                    serviceID: auftragszeit.serviceID,
                    datum: datum!,
                    datumStrEN: datumStrEn,
                    datumStrCH: datumStrCh,
                    mitarbeiter: arbeiterName,
                    auftrag: auftragObj,
                    zeiten: zeitVollstaendig,
                    startzeit: zeitBeginn,
                    endzeit: zeitEnde,
                    zeitMenge: +auftragszeit.duration,
                    arbeitsbezeichnung: auftragszeit.activityDescription,
                    spesenArt: spesen,
                    spesenMenge: spesenMengeEinheit,
                    spesenBetrag,
                    arbeitsort: auftragszeit.workPlace!,
                    notizen: auftragszeit.note!,
                    kulanz: auftragszeit.goodWill!,
                    uploaded: auftragszeit.uploadedToTimeControlEntries,
                    isTimeEntry,
                };
                // Hier werden die Tageszeiten summiert
                this.totalTagesAufwand += Number(zeile.zeitMenge);
                times.push(zeile);
            }
        }

        if (this.currTabIndex == 2) {
            if (this.filtervalue == 'Abgeschlossene Tage') {
                times.forEach(zz => {
                    if (this.svcSA.getTimeControlDayState(zz.datumStrEN) === 'lock') {
                        timeControl.push(zz);
                    }
                });
            } else if (this.filtervalue == 'Offene Tage') {
                times.forEach(zz => {
                    if (this.svcSA.getTimeControlDayState(zz.datumStrEN) !== 'lock') {
                        timeControl!.push(zz);
                    }
                });
            } else {
                timeControl.push(...times);
            }

        }

        this.zeitZeilen$.next(times);
        this.zeitKontrollZeilen$.next(timeControl);
    }

    private async handleRowSelected(): Promise<void> {
        if (this.currZeitZeile) {
            this.currTimeEntry = this.svcSA.getDBAuftragsZeiten(false).filter((az) => az.servicePosID == this.currZeitZeile?.servicePosID)[0];

            if (this.currTimeEntry) {
                this.isTimeSelected$.next(true);
                if (this.currTimeEntry.serviceID) { // die serviceID ist nicht gesetzt, wenn auf einem beliebigen Objekt Zeit erfasst wurde.
                    this.currTerminFinished$.next(this.svcSA.getDBTermine().find((auf) => auf.serviceID === this.currZeitZeile?.serviceID)?.serviceComplete === 1);
                } else {
                    this.currTerminFinished$.next(false);
                }
            }

            const res = await this.svcSA.getTimeEntryByServicePosID(this.currZeitZeile?.servicePosID);
            if (res) {
                const timeControlEntry = res as ServiceAppModels.SA_TimeControlEntry;
                if (timeControlEntry) {
                    this.timeUploaded$.next(timeControlEntry.uploaded);
                } else {
                    this.timeUploaded$.next(false);
                }
            } else {
                this.timeUploaded$.next(false);
            }
            this.currDayClosed$.next(this.svcSA.getTimeControlDayState(this.currZeitZeile.datumStrEN) === 'lock');
        } else {
            this.currTimeEntry = undefined;
        }
    }

}
